import React, { useContext, useState } from 'react';
import {
  WDivider,
  WIconButton,
  WMenu,
  WMenuItem,
  WModal,
  WStack,
  WTypography,
} from '../../wrappers/wrapper';
import styles from './article-content.module.scss';
import { TArticle, TLike, TUser } from '../../../models/common';
import { MoreHorizRounded, Favorite, FavoriteBorder, ChatBubbleOutline } from '@mui/icons-material';
import ViewProfile from '../../prefabs/view-profile-popover/view-profile-popover';
import { ArticleOwner } from '../article-owner/article-owner';
import SeeMoreSeeLessComponent from '../../molecules/see-more-see-less/see-more-see-less';
import PostLike from '../../prefabs/post-like/post-like';
import CommentList from '../../prefabs/comment-list/comment-list';
import PostLikeMini from '../post-like-mini/post-like-mini';
import { UserContext } from 'contexts/userContext';
import ArticleMoreMenu from '../article-more-menu/article-more-menu';
import ArticleContentSections from '../../molecules/article-content-sections/article-content-sections';

interface ArticleContentProps {
  data: TArticle;
  anchorEl: HTMLElement | null;
  open: boolean;
  isLiked: boolean;
  likesMini: TLike[];
  totalLikeCount: number;
  totalCommentCount: number;
  handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleClose: () => void;
  showComments: boolean;
  setShowComments: React.Dispatch<React.SetStateAction<boolean>>;
  onLikeClick?: () => void;
  deleteArticle: (article_id: string) => any;
  onCommentedChanged: () => void;
  className?: string;
  hideInteractions?: boolean;
}

const ArticleContent: React.FC<ArticleContentProps> = ({
  data,
  anchorEl,
  open,
  isLiked,
  likesMini,
  totalLikeCount,
  totalCommentCount,
  handleClick,
  handleClose,
  showComments,
  setShowComments,
  onLikeClick,
  deleteArticle,
  onCommentedChanged,
  className,
  hideInteractions,
}) => {
  const { user } = useContext(UserContext);

  const [showLikes, setShowLikes] = useState<boolean>(false);
  const [profileUserId, setProfileUserId] = useState('');
  const [isProfileBoxVisible, setIsProfileBoxVisible] = useState(false);
  const isOwner = user?._id === (data.publisher as TUser)._id;

  const onClickPostLikeMini = () => {
    setShowLikes(true);
  };

  const handleClickPostLike = (id: string) => {
    setShowLikes(false);
    setProfileUserId(id);
    setIsProfileBoxVisible(true);
  };

  const handleCloseLikesModal = () => {
    setShowLikes(false);
  };

  const classes = `${styles.articleContainer} ${className || ''}`.trim();
  return (
    <WStack className={classes}>
      {data.cover_image && (
        <div className={styles.coverImage}>
          <img src={data.cover_image} alt={data.title} />
        </div>
      )}

      <div className={styles.contentBox}>
        {!hideInteractions && (
          <div className={styles.owner}>
            <ArticleOwner
              user={data.publisher as TUser}
              fromNowValue={data.created_at}
              onClick={() => {
                setProfileUserId((data.publisher as TUser)._id!);
                setIsProfileBoxVisible(true);
              }}
            />
            {isOwner && (
              <WIconButton className={styles['card-more-icon']} onClick={handleClick}>
                <MoreHorizRounded fontSize="small" />
              </WIconButton>
            )}
            <ArticleMoreMenu
              article={data}
              open={open}
              anchorEl={anchorEl}
              deleteArticle={deleteArticle}
              handleClose={handleClose}
            />
          </div>
        )}

        {data.title && <h2 className={styles.title}>{data.title}</h2>}
        {data.content && (
          <div 
            className={styles.content}
            dangerouslySetInnerHTML={{ __html: data.content }}
          />
        )}

        <div className={styles.content}>
          {data.content_array ? (
            <ArticleContentSections contentArray={data.content_array} />
          ) : (
            <SeeMoreSeeLessComponent htmlContent={data.content || ''} maxLength={600} />
          )}
          {data.youtube_id && (
            <iframe
              title="youtube"
              className={styles['content-youtube-video']}
              src={`https://www.youtube.com/embed/${data.youtube_id}`}
            />
          )}
        </div>
      </div>

      {!hideInteractions && <WDivider className={styles.divider} />}

      {!hideInteractions && (
        <WStack direction="row" justifyContent="space-between" className={styles.bottom}>
          <div className={styles.actions}>
            {isLiked ? (
              <Favorite onClick={onLikeClick} fontSize="small" />
            ) : (
              <FavoriteBorder onClick={onLikeClick} fontSize="small" />
            )}
            <ChatBubbleOutline onClick={() => setShowComments(!showComments)} fontSize="small" />
          </div>
          <WStack direction="row" spacing={2}>
            <PostLikeMini
              likes={likesMini}
              totalCount={totalLikeCount}
              onClick={onClickPostLikeMini}
            />
            {!!totalCommentCount && (
              <WTypography
                onClick={() => setShowComments(!showComments)}
                className={styles['content-comments-text']}
              >{`${totalCommentCount} comment${totalCommentCount !== 1 ? 's' : ''}`}</WTypography>
            )}
          </WStack>
        </WStack>
      )}
      {showComments && !hideInteractions && <CommentList article={data} onCommentedChanged={onCommentedChanged} />}

      {isProfileBoxVisible && !hideInteractions && (
        <ViewProfile
          showEditButton={false}
          user_id={profileUserId}
          onClose={() => setIsProfileBoxVisible(false)}
        />
      )}
      <WModal
        disableAutoFocus={true}
        open={showLikes}
        onClose={handleCloseLikesModal}
        className={styles['likes-modal']}
      >
        <PostLike onClose={handleCloseLikesModal} id={data._id!} onClick={handleClickPostLike} />
      </WModal>
    </WStack>
  );
};

export default ArticleContent;
